import React from 'react';
import { motion } from 'framer-motion';
import { Tv, Users, Wifi, ExternalLink, Calendar, Music, Trophy, Target, Star, Award, Globe, Medal, Flame, Shield } from 'lucide-react';
import { useLanguage } from './LanguageContext';
export function Gallery() {
  const {
    t
  } = useLanguage();
  const topScorers = [{
    rank: 1,
    name: 'Miroslav Klose',
    goals: 71
  }, {
    rank: 2,
    name: 'Gerd Müller',
    goals: 68
  }, {
    rank: 3,
    name: 'Lukas Podolski',
    goals: 49
  }, {
    rank: 4,
    name: 'Jürgen Klinsmann',
    goals: 47
  }, {
    rank: 5,
    name: 'Rudi Völler',
    goals: 47
  }, {
    rank: 6,
    name: 'Karl-Heinz Rummenigge',
    goals: 45
  }, {
    rank: 7,
    name: 'Thomas Müller',
    goals: 44
  }, {
    rank: 8,
    name: 'Uwe Seeler',
    goals: 43
  }, {
    rank: 9,
    name: 'Michael Ballack',
    goals: 42
  }, {
    rank: 10,
    name: 'Oliver Bierhoff',
    goals: 37
  }];
  const records = [{
    icon: <Trophy size={24} className="text-germany-gold" data-id="element-94" />,
    title: 'Biggest Win',
    value: '16–0',
    subtitle: 'vs Russia (1912)'
  }, {
    icon: <Star size={24} className="text-germany-gold" data-id="element-95" />,
    title: 'Most Capped',
    value: '150',
    subtitle: 'Lothar Matthäus'
  }, {
    icon: <Award size={24} className="text-germany-gold" data-id="element-96" />,
    title: 'Trophy Cabinet',
    value: '7 Titles',
    subtitle: '4 WC, 3 Euro'
  }, {
    icon: <Target size={24} className="text-germany-gold" data-id="element-97" />,
    title: 'Golden Era',
    value: 'Legends',
    subtitle: 'Beckenbauer, Müller, Klose'
  }, {
    icon: <Globe size={24} className="text-germany-gold" data-id="element-98" />,
    title: 'FIFA Ranking',
    value: 'Top 15',
    subtitle: 'Consistently world elite'
  }, {
    icon: <Medal size={24} className="text-germany-gold" data-id="element-99" />,
    title: 'World Cup Best',
    value: '4x Champions',
    subtitle: '1954, 1974, 1990, 2014'
  }, {
    icon: <Flame size={24} className="text-germany-gold" data-id="element-100" />,
    title: 'All-Time Wins',
    value: '580+',
    subtitle: 'Since 1908'
  }, {
    icon: <Shield size={24} className="text-germany-gold" data-id="element-101" />,
    title: 'Euro Streak',
    value: '13 Straight',
    subtitle: 'Consecutive Euros'
  }];
  const quickLinks = [{
    icon: <Tv size={18} data-id="element-102" />,
    label: t('fanHq.whereToWatch'),
    desc: t('fanHq.whereToWatchDesc'),
    link: 'https://www.foxsports.com/soccer/fifa-world-cup'
  }, {
    icon: <Users size={18} data-id="element-103" />,
    label: t('fanHq.fanZones'),
    desc: t('fanHq.fanZonesDesc'),
    link: 'https://www.fifa.com/en/tournaments/mens/worldcup/canadamexicousa2026/host-cities/fan-information-channels'
  }, {
    icon: <Globe size={18} data-id="element-104" />,
    label: t('fanHq.fifaRanking'),
    desc: t('fanHq.fifaRankingDesc'),
    link: 'https://www.fifa.com/en/world-rankings'
  }, {
    icon: <Calendar size={18} data-id="element-105" />,
    label: t('fanHq.fullSchedule'),
    desc: t('fanHq.fullScheduleDesc'),
    link: 'https://www.espn.com/soccer/schedule/_/league/fifa.world'
  }];
  return <section className="py-12 md:py-24 bg-[#0a0a0a]" id="fan-hq" data-id="element-106">
      <div className="container mx-auto px-4 md:px-12" data-id="element-107">
        {/* Section Header — consolidated */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-10 md:mb-16" data-id="element-108">
          <h2 className="text-germany-platinum font-oswald uppercase tracking-widest text-xs md:text-sm font-bold mb-2" data-id="element-109">
            {t('fanHq.label')}
          </h2>
          <h3 className="text-3xl md:text-5xl font-oswald font-bold uppercase mb-4" data-id="element-110">
            {t('fanHq.title1')}{' '}
            <span className="text-germany-gold" data-id="element-111">{t('fanHq.title2')}</span>
          </h3>
          <div className="w-16 h-[2px] bg-gradient-to-r from-germany-platinum/0 via-germany-platinum to-germany-platinum/0 mx-auto" data-id="element-112"></div>
        </motion.div>
        {/* Fan Essentials — Anthem + Top Scorers */}
        <div className="mb-8 md:mb-12" data-id="element-113">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="flex items-center gap-3 mb-6" data-id="element-114">
            <Music className="text-germany-gold" size={24} data-id="element-115" />
            <h4 className="font-oswald text-2xl md:text-3xl font-bold uppercase text-white" data-id="element-116">
              Fan <span className="text-germany-red" data-id="element-117">Essentials</span>
            </h4>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6" data-id="element-118">
            {/* Deutschlandlied — National Anthem */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4
          }} className="bg-[#111] border border-germany-platinum/20 rounded-xl p-6 md:p-8 relative overflow-hidden" data-id="element-119">
              <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-germany-platinum/0 via-germany-platinum/50 to-germany-platinum/0" data-id="element-120"></div>
              <div className="flex items-center gap-2 mb-1 text-germany-gold" data-id="element-121">
                <Music size={18} data-id="element-122" />
                <h5 className="font-oswald text-xl md:text-2xl font-bold uppercase text-germany-platinum" data-id="element-123">
                  Deutschlandlied
                </h5>
              </div>
              <p className="text-xs text-germany-platinum/60 font-oswald uppercase tracking-widest mb-5" data-id="element-124">
                {t('fanHq.nationalAnthem')}
              </p>
              <p className="text-sm md:text-base text-gray-300 whitespace-pre-line italic mb-4 leading-relaxed" data-id="element-125">
                "Einigkeit und Recht und Freiheit{'\n'}Für das deutsche
                Vaterland!{'\n'}
                Danach lasst uns alle streben{'\n'}Brüderlich mit Herz und
                Hand!"
              </p>
              <p className="text-xs text-gray-500 whitespace-pre-line mb-6 leading-relaxed" data-id="element-126">
                Unity and justice and freedom{'\n'}For the German fatherland!
                {'\n'}Let us all strive for this{'\n'}Brotherly with heart and
                hand!
              </p>
              <a href="https://www.youtube.com/watch?v=JH7rhGOyB0g" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-germany-red/20 border border-germany-red/30 text-germany-red hover:bg-germany-red hover:text-white px-5 py-2.5 rounded-lg font-oswald uppercase text-xs font-bold tracking-wider transition-all duration-300 min-h-[44px]" data-id="element-127">
                <ExternalLink size={14} data-id="element-128" />
                {t('fanHq.watchYoutube')}
              </a>
            </motion.div>
            {/* All-Time Top Scorers */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: 0.1
          }} className="bg-[#111] border border-white/5 rounded-xl p-6 md:p-8" data-id="element-129">
              <div className="flex items-center gap-2 mb-5" data-id="element-130">
                <Target size={18} className="text-germany-gold" data-id="element-131" />
                <h5 className="font-oswald text-xl md:text-2xl font-bold uppercase text-white" data-id="element-132">
                  All-Time Top{' '}
                  <span className="text-germany-gold" data-id="element-133">Scorers</span>
                </h5>
              </div>
              <div className="space-y-0" data-id="element-134">
                {topScorers.map((player, i) => <div key={i} className={`flex items-center justify-between py-2.5 ${i < topScorers.length - 1 ? 'border-b border-white/5' : ''}`} data-id="element-135">
                    <div className="flex items-center gap-3" data-id="element-136">
                      <span className="font-oswald text-sm font-bold text-germany-gold w-6 text-right" data-id="element-137">
                        {player.rank}
                      </span>
                      <span className={`font-oswald text-sm font-bold uppercase ${i === 0 ? 'text-white' : 'text-gray-300'}`} data-id="element-138">
                        {player.name}
                      </span>
                    </div>
                    <span className={`font-oswald text-sm font-bold ${i === 0 ? 'text-germany-gold' : 'text-gray-500'}`} data-id="element-139">
                      {player.goals} goals
                    </span>
                  </div>)}
              </div>
            </motion.div>
          </div>
        </div>
        {/* Record Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 mb-8 md:mb-12" data-id="element-140">
          {records.map((record, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: index * 0.08
        }} className="bg-[#111] border border-germany-platinum/10 rounded-xl p-5 md:p-6 hover:border-germany-platinum/30 transition-colors relative overflow-hidden" data-id="element-141">
              <div className="absolute top-0 right-0 w-16 h-16 bg-germany-platinum/[0.03] rounded-bl-full" data-id="element-142"></div>
              <div className="mb-3" data-id="element-143">{record.icon}</div>
              <p className="text-xs text-germany-platinum/70 font-oswald uppercase tracking-widest mb-1" data-id="element-144">
                {record.title}
              </p>
              <p className="font-oswald text-2xl md:text-3xl font-bold text-germany-platinum mb-1" data-id="element-145">
                {record.value}
              </p>
              <p className="text-xs text-gray-500" data-id="element-146">{record.subtitle}</p>
            </motion.div>)}
        </div>
        {/* Quick Links Grid */}
        <div data-id="element-147">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="flex items-center gap-3 mb-6" data-id="element-148">
            <ExternalLink className="text-germany-gold" size={24} data-id="element-149" />
            <h4 className="font-oswald text-2xl md:text-3xl font-bold uppercase text-white" data-id="element-150">
              {t('fanHq.quickLinks')}{' '}
              <span className="text-germany-red" data-id="element-151">
                {t('fanHq.quickLinksHighlight')}
              </span>
            </h4>
          </motion.div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4" data-id="element-152">
            {quickLinks.map((item, index) => <motion.a key={index} href={item.link} target="_blank" rel="noopener noreferrer" initial={{
            opacity: 0,
            y: 15
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.3,
            delay: index * 0.08
          }} className="bg-[#111] border border-white/5 rounded-xl p-4 md:p-5 hover:border-germany-gold/30 transition-all group flex flex-col min-h-[44px]" data-id="element-153">
                <div className="text-germany-gold mb-2 group-hover:scale-110 transition-transform origin-left" data-id="element-154">
                  {item.icon}
                </div>
                <h5 className="font-oswald text-xs md:text-sm font-bold uppercase text-white mb-1 leading-tight" data-id="element-155">
                  {item.label}
                </h5>
                <p className="text-[10px] md:text-xs text-gray-500 flex-grow" data-id="element-156">
                  {item.desc}
                </p>
                <ExternalLink size={12} className="text-gray-600 mt-2" data-id="element-157" />
              </motion.a>)}
          </div>
        </div>
      </div>
    </section>;
}